/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { api } from '@/lib/api';

/**
 * Web push, from the browser's side.
 *
 * **The decisions live in the pure functions at the top of this file, and the
 * browser calls live at the bottom.** That split is the whole reason this is
 * testable: what a person is shown depends on four facts — does this browser
 * expose the APIs, does the server have keys, what did they answer last time,
 * and are they on iOS — and those four produce six different sentences (§6 of
 * the plan). Wiring that into a component would make it provable only by hand on
 * six devices.
 *
 * The one thing to understand before changing anything here: **a browser will
 * not let us ask twice.** `Notification.requestPermission()` returns `denied`
 * for ever after a single refusal and shows nothing. There is no retry, no
 * second prompt, and only the person can undo it in browser settings. Every
 * "ask carefully" rule below follows from that.
 */

/** The seven states §6 of the plan enumerates, and nothing outside this list. */
export type PushState =
  /** Subscribed on this device. */
  | 'on'
  /** Supported, never asked, and asking would work. */
  | 'off'
  /** Asked once and refused. We cannot ask again; only they can undo it. */
  | 'denied'
  /** iOS, and the APIs are absent — i.e. a Safari tab, where a prompt does nothing. */
  | 'ios-tab'
  /** iOS below 16.4. Apple did not deliver web push at all before that. */
  | 'ios-old'
  /** No service worker, or no Push API, or the server has no VAPID keys. */
  | 'unsupported';

export type Platform = 'ios' | 'android' | 'desktop';

/** What the state resolver needs to know. Every field is a fact, never a preference. */
export interface PushFacts {
  /** `serviceWorker` and `PushManager` both present. */
  supported: boolean;
  /** The server has VAPID keys. Without them there is nothing to subscribe to. */
  configured: boolean;
  permission: NotificationPermission;
  platform: Platform;
  /** Major.minor as a number — 16.4 is the line Apple drew. Null off iOS. */
  iosVersion: number | null;
  /** A subscription exists in this browser right now. */
  subscribed: boolean;
}

/**
 * Read the platform off a user-agent string.
 *
 * **iPadOS 13+ lies**: it reports itself as a Macintosh, and the only reliable
 * tell is that it has a touch screen. Getting this wrong would show a Mac user
 * the Add to Home Screen steps, or — worse — hide them from an iPad user who
 * genuinely needs them.
 */
export function detectPlatform(ua: string, maxTouchPoints = 0): Platform {
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/macintosh/i.test(ua) && maxTouchPoints > 1) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'desktop';
}

/**
 * The iOS version, as a number, or null.
 *
 * 16.4 is where Apple started delivering web push, and it is delivered **only**
 * to an installed app. `16_4` in the UA becomes 16.4; `16_10` becomes 16.1,
 * which is wrong by a hair and right by the only comparison anybody makes here
 * (`>= 16.4`), because 16.10 does not exist — Apple's minors have never reached
 * double digits.
 */
export function iosVersion(ua: string): number | null {
  const m = /OS (\d+)[._](\d+)/i.exec(ua);
  if (!m) return null;
  return Number(`${m[1]}.${m[2]}`);
}

/**
 * The one function that decides what a person is shown.
 *
 * **The capability decides; the user agent only decides which explanation.**
 * That distinction is the whole of this function and it was got wrong first
 * time. The first version read "iPhone, and not installed" off the user agent
 * and refused to offer the button — which meant Chrome's device toolbar, where
 * the UA *is* an iPhone and the browser is Chrome, showed the Add to Home Screen
 * steps and no way to turn anything on. Nobody testing on a laptop could enable
 * push at all.
 *
 * It works because **iOS Safari does not expose the APIs in a tab.** `Notification`
 * and `PushManager` are undefined there and only appear once the app has been
 * added to the Home Screen — so `supported` already answers "can this browser
 * prompt", exactly and without guessing. The platform is then used for one thing
 * only: on iOS, *missing* means "install it and it will work", which is a fix;
 * everywhere else missing means unsupported, which is not.
 *
 * `denied` still outranks everything below it: once refused, the Add to Home
 * Screen steps would not help either, and telling somebody to install an app
 * that still will not ask them is worse than saying plainly what happened.
 */
export function resolveState(f: PushFacts): PushState {
  // No VAPID keys is the one impossibility that is ours rather than the
  // browser's, and no instruction to the person can fix it.
  if (!f.configured) return 'unsupported';

  if (!f.supported) {
    if (f.platform === 'ios') {
      // Below 16.4 Apple did not deliver web push at all, installed or not, so
      // the Add to Home Screen steps would be a waste of somebody's time.
      return f.iosVersion !== null && f.iosVersion < 16.4 ? 'ios-old' : 'ios-tab';
    }
    return 'unsupported';
  }

  if (f.subscribed && f.permission === 'granted') return 'on';
  if (f.permission === 'denied') return 'denied';
  return 'off';
}

/**
 * Whether flipping the switch in the account menu can achieve anything.
 *
 * `on` and `off` are the only two live states. Everywhere else the switch is
 * shown dimmed and the row opens the dialog instead — the plan's rule that a
 * control which silently fails is worse than no control, and a switch that flips
 * back by itself is how somebody decides this is broken.
 */
export function isActionable(state: PushState): boolean {
  return state === 'on' || state === 'off';
}

/* ------------------------------------------------------------------ *
 * The one-time introduction
 * ------------------------------------------------------------------ */

/**
 * **A version, not a boolean.** A later change can introduce itself again by
 * raising this, without reusing a key whose meaning has moved — the same lesson
 * as `kanbanBoardSort`, which is still called *kanban* because renaming a
 * storage key silently discards what everyone had saved.
 */
export const INTRO_VERSION = 1;
export const INTRO_KEY = 'qollabPushIntro';

/**
 * Whether to show the introduction card.
 *
 * Three ways to answer no, and each of them is a rule from §6.1:
 *
 *  - they have already seen this version. "Not now" means never, not next week
 *  - push is already on for them. Nothing reads as more automated than being
 *    told about a thing you are already using
 *  - there is nothing to introduce — an unsupported browser or an install with
 *    no VAPID keys would get a card advertising something it cannot do
 */
export function shouldShowIntro(state: PushState, seen: string | null): boolean {
  if (state === 'on' || state === 'unsupported') return false;
  // **`Number.isFinite`, not a bare comparison.** `Number('yes')` is NaN, and
  // every comparison with NaN is false — so a junk value left by an extension,
  // an old build or a hand-edited profile would have suppressed the card for
  // ever, silently, on exactly the machines nobody would think to check. An
  // unreadable value means "we do not know", and the answer to that is to show
  // it once.
  const version = Number(seen);
  return !Number.isFinite(version) || version < INTRO_VERSION;
}

/* ------------------------------------------------------------------ *
 * Talking to the browser
 * ------------------------------------------------------------------ */

/**
 * The VAPID public key travels as base64url and `subscribe()` wants raw bytes.
 *
 * `atob` needs standard base64, so the two url-safe substitutions are undone and
 * the padding put back. This is boilerplate every web push client carries; it is
 * here rather than inlined because getting it subtly wrong produces a subscribe
 * that fails only on some browsers.
 */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  // The buffer is allocated explicitly so the type is `Uint8Array<ArrayBuffer>`
  // and not `ArrayBufferLike`: `applicationServerKey` will not take a view that
  // might be over a SharedArrayBuffer, and the plain constructor is widened to
  // exactly that.
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Whether this browser can prompt at all.
 *
 * **This is also the iOS test**, which is why `resolveState` needs no
 * `standalone` flag: iOS Safari does not expose `Notification` or `PushManager`
 * in a tab, and exposes both once the app is on the Home Screen. Asking the
 * browser what it has beats asking the user agent what it claims to be — a user
 * agent can be spoofed by a device toolbar, and was.
 */
export function supported(): boolean {
  return typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
    && typeof window !== 'undefined'
    && 'PushManager' in window
    && 'Notification' in window;
}

let registration: ServiceWorkerRegistration | null = null;

/**
 * Register the worker, once.
 *
 * **Not at module load.** Registering a service worker on an unauthenticated
 * page would put one on the sign-in screen, and the browser keeps it. This is
 * called from the account menu and the introduction card, both of which only
 * exist behind a session.
 */
export async function register(): Promise<ServiceWorkerRegistration | null> {
  if (!supported()) return null;
  if (registration) return registration;
  try {
    registration = await navigator.serviceWorker.register('/sw.js');
    // `.ready` rather than the registration alone: a worker that is installed
    // but not yet active cannot be subscribed through, and the failure is an
    // intermittent one that only shows on a first-ever load.
    await navigator.serviceWorker.ready;
    return registration;
  } catch {
    // A worker can fail to register for reasons that have nothing to do with
    // this person — a private window, an enterprise policy. It is not an error
    // to show; `resolveState` will report `unsupported` and the dialog explains.
    return null;
  }
}

export interface ServerKey { enabled: boolean; key: string }

export function fetchKey(): Promise<ServerKey> {
  return api.get<ServerKey>('/api/push/key');
}

/**
 * The subscription this browser already has, if any.
 *
 * **`getRegistration`, not `register`** — asking is a read and must not have the
 * side effect of installing a worker. Reading through `register()` would put a
 * service worker on the machine of everybody who merely opened the account menu,
 * and on the machine of everybody signing out, neither of whom asked for one.
 */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!supported()) return null;
  try {
    const reg = registration ?? (await navigator.serviceWorker.getRegistration('/')) ?? null;
    if (!reg) return null;
    registration = reg;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Turn it on. **This is the only thing in the app that shows a permission prompt**,
 * and it runs from a click, never from a page load — a prompt nobody invited is
 * the reason browsers now bury the control after one refusal.
 *
 * Returns false rather than throwing when the person says no: a refusal is an
 * answer, not a fault, and turning it into an error banner would be telling
 * somebody off for a choice they were offered.
 */
export async function enable(): Promise<boolean> {
  if (!supported()) return false;
  const { enabled, key } = await fetchKey();
  if (!enabled || !key) return false;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  const reg = await register();
  if (!reg) return false;

  // `userVisibleOnly` is not optional in Chrome — it is the promise that every
  // push shows a notification, which is why sw.js always calls
  // showNotification even for a payload it could not parse.
  const subscription = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key),
  });

  await api.post('/api/push/subscribe', subscription.toJSON() as Record<string, unknown>);
  return true;
}

/**
 * Turn it off — **both halves, and in this order**.
 *
 * The browser subscription goes first because that is the one that cannot be
 * recreated from our side; if the server call then fails, the person is left
 * unsubscribed with a stale row, and the next 410 prunes it. Doing it the other
 * way round leaves the browser holding a subscription the server has forgotten,
 * and re-subscribing returns the same endpoint — so the row never comes back and
 * they cannot turn it on again.
 */
export async function disable(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  const { endpoint } = sub;
  try {
    await sub.unsubscribe();
  } catch {
    // Already gone as far as the browser is concerned. The row still has to go.
  }
  await api.delete('/api/push/subscribe', { endpoint });
}

/**
 * Called on every app load where permission is already granted.
 *
 * **Browsers rotate endpoints**, and a subscription we never refresh dies
 * quietly — the person keeps seeing the switch on while nothing arrives, which
 * is the worst of the possible failures because nothing anywhere says so. The
 * server upserts on the endpoint, so this is free to run every time.
 *
 * Silent on failure, deliberately: this runs without anybody asking for it, and
 * it must never put an error in front of somebody who was reading their board.
 */
export async function refresh(): Promise<void> {
  try {
    if (!supported() || Notification.permission !== 'granted') return;
    const sub = await currentSubscription();
    if (!sub) return;
    await api.post('/api/push/subscribe', sub.toJSON() as Record<string, unknown>);
  } catch {
    /* nothing here is worth interrupting anybody for */
  }
}

/**
 * Signing out.
 *
 * **A push must not keep arriving on a device somebody has signed out of.** It
 * carries a circle name and a task title, and a phone screen is not an inbox —
 * §2 of the plan. This is done from the client rather than in `/auth/logout`
 * because the server has no way to know *which* device is leaving: the session
 * cookie says who, never what they are holding.
 *
 * Awaited by the caller before it navigates away, and it swallows everything —
 * signing out must never be blocked by a failure to unsubscribe.
 */
export async function signOutCleanup(): Promise<void> {
  try {
    await disable();
  } catch {
    /* leaving is more important than tidying */
  }
}
